import { useMemo, useState, type MouseEvent } from 'react';
import { normalizarNombre } from '@eventos-cien/shared';
import type { Invitacion } from '@eventos-cien/shared';
import { useAuth } from '../auth/AuthProvider';
import { Callout } from '../components/Callout';
import { Card } from '../components/Card';
import { Chip } from '../components/Chip';
import { Eyebrow } from '../components/Eyebrow';
import { TextField } from '../components/TextField';
import { useDispositivoTactil } from '../lib/useDispositivoTactil';
import { EscanerQR, extraerToken } from '../puerta/EscanerQR';
import { marcarAsistencia } from '../puerta/marcarAsistencia';
import { TarjetaAsistencia } from '../puerta/TarjetaAsistencia';
import { usePanelData } from './PanelDataProvider';

type Tab = 'escanear' | 'buscar';

export function CheckInScreen() {
  const { eventoId, sinConexion, invitaciones, mesas, errorInvitaciones } = usePanelData();
  const tactil = useDispositivoTactil();
  // Táctil (tablet/celular): abre directo en escáner. Escritorio: abre en buscador con foco.
  const [tab, setTab] = useState<Tab>(tactil ? 'escanear' : 'buscar');
  const [tokenActivo, setTokenActivo] = useState<string | null>(null);

  const mesaDe = useMemo(() => {
    const m = new Map(mesas.map((x) => [x.id, { numero: x.numero, nombre: x.nombre }]));
    return (mesaId: string | null) => (mesaId ? (m.get(mesaId) ?? null) : null);
  }, [mesas]);

  const invitacionActiva = tokenActivo ? invitaciones.find((i) => i.token === tokenActivo) : null;

  return (
    <div className="w-full max-w-full space-y-4">
      {errorInvitaciones && <Callout tono="error">{errorInvitaciones}</Callout>}

      {sinConexion && (
        <Callout tono="alerta">
          Sin señal: trabajando con la copia local. Se sincroniza sola al reconectar.
        </Callout>
      )}

      {invitacionActiva ? (
        <TarjetaAsistencia
          eventoId={eventoId}
          invitacion={invitacionActiva}
          mesa={mesaDe(invitacionActiva.mesaId)}
          onCerrar={() => setTokenActivo(null)}
        />
      ) : (
        <>
          <div className="flex w-full gap-2">
            <Chip activo={tab === 'escanear'} onClick={() => setTab('escanear')}>
              Escanear
            </Chip>
            <Chip activo={tab === 'buscar'} onClick={() => setTab('buscar')}>
              Buscar
            </Chip>
          </div>

          {tab === 'escanear' ? (
            <Escanear invitaciones={invitaciones} pantallaCompleta={tactil} onEncontrado={setTokenActivo} />
          ) : (
            <Buscar
              eventoId={eventoId}
              invitaciones={invitaciones}
              mesaDe={mesaDe}
              tactil={tactil}
              onElegir={setTokenActivo}
            />
          )}
        </>
      )}
    </div>
  );
}

function Escanear({
  invitaciones,
  pantallaCompleta,
  onEncontrado,
}: {
  invitaciones: Invitacion[];
  pantallaCompleta?: boolean;
  onEncontrado: (token: string) => void;
}) {
  const [aviso, setAviso] = useState<string | null>(null);

  function onLeido(texto: string) {
    const token = extraerToken(texto);
    const inv = invitaciones.find((i) => i.token === token);
    if (!inv) {
      setAviso('No encontramos ese boleto. Prueba con la pestaña Buscar.');
      setTimeout(() => setAviso(null), 2500);
      return;
    }
    onEncontrado(token);
  }

  return (
    <div className="w-full space-y-3">
      <EscanerQR activo onLeido={onLeido} pantallaCompleta={pantallaCompleta} />
      {aviso && <Callout tono="alerta">{aviso}</Callout>}
    </div>
  );
}

function Buscar({
  eventoId,
  invitaciones,
  mesaDe,
  tactil,
  onElegir,
}: {
  eventoId: string;
  invitaciones: Invitacion[];
  mesaDe: (mesaId: string | null) => { numero: number; nombre: string } | null;
  tactil: boolean;
  onElegir: (token: string) => void;
}) {
  const { user } = useAuth();
  const [q, setQ] = useState('');
  const [dando, setDando] = useState<string | null>(null);

  const resultados = useMemo(() => {
    const digitos = q.replace(/\D/g, '');
    const norm = normalizarNombre(q);
    if (!norm && digitos.length < 2) return [];
    return invitaciones
      .filter((i) => i.estado === 'registrada')
      .filter((i) => {
        // Se recalcula en vivo (no solo `nombreNormalizado`, que solo cubre al
        // titular) para poder encontrar tambien al acompañante — la
        // asistencia se marca por persona, ambas deben ser buscables.
        const porTitular = norm && normalizarNombre(i.invitado.nombre || '').includes(norm);
        const porAcompanante =
          norm && !!i.invitado.acompanante && normalizarNombre(i.invitado.acompanante).includes(norm);
        const porTelefono = digitos.length >= 2 && i.telefonoUlt4?.includes(digitos);
        return porTitular || porAcompanante || porTelefono;
      })
      .slice(0, 30);
  }, [invitaciones, q]);

  async function marcarPersona(
    e: MouseEvent,
    token: string,
    quien: 'titular' | 'acompanante',
    presente: boolean,
  ) {
    e.stopPropagation();
    const clave = `${token}:${quien}`;
    setDando(clave);
    try {
      await marcarAsistencia(eventoId, token, quien, presente, user?.uid ?? null);
    } finally {
      setDando(null);
    }
  }

  return (
    <div className="w-full space-y-3">
      <TextField
        label="Buscar"
        type="search"
        inputMode="search"
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        placeholder={tactil ? 'Últimos 4 dígitos o nombre' : 'Nombre o últimos 4 dígitos'}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <ul className="w-full space-y-2">
        {resultados.map((i) => (
          <li key={i.token} className="w-full">
            <Card
              role="button"
              tabIndex={0}
              onClick={() => onElegir(i.token)}
              onKeyDown={(e) => e.key === 'Enter' && onElegir(i.token)}
              className={[
                'flex w-full cursor-pointer flex-col gap-3 text-left transition hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between',
                tactil ? 'p-4 sm:p-5' : 'p-3.5 sm:p-4',
              ].join(' ')}
            >
              <span className="flex min-w-0 flex-col gap-0.5 sm:flex-1 sm:pr-1">
                <strong className={['truncate text-texto', tactil ? 'text-base' : 'text-sm'].join(' ')}>
                  {i.invitado.nombre}
                </strong>
                {i.invitado.acompanante && (
                  <span className="truncate text-xs text-texto/50">+ {i.invitado.acompanante}</span>
                )}
                {i.mesaId && (
                  <span className="truncate text-xs text-texto/50">Mesa {mesaDe(i.mesaId)?.numero}</span>
                )}
              </span>
              {/* Cada persona se marca por separado: un "Ya entró" combinado
                  daba a entender que ambos habían llegado cuando solo era uno. */}
              <span className="flex shrink-0 flex-col items-start gap-1 sm:items-end">
                <Eyebrow>Asistencia</Eyebrow>
                <span className="flex w-full flex-wrap items-center gap-1.5 sm:w-auto sm:justify-end">
                  <CasillaAsistencia
                    etiqueta="Titular"
                    presente={i.asistencia.titular.presente}
                    cargando={dando === `${i.token}:titular`}
                    tactil={tactil}
                    onClick={(e) =>
                      void marcarPersona(e, i.token, 'titular', !i.asistencia.titular.presente)
                    }
                  />
                  {i.invitado.acompanante && (
                    <CasillaAsistencia
                      etiqueta="Acompañante"
                      presente={i.asistencia.acompanante.presente}
                      cargando={dando === `${i.token}:acompanante`}
                      tactil={tactil}
                      onClick={(e) =>
                        void marcarPersona(e, i.token, 'acompanante', !i.asistencia.acompanante.presente)
                      }
                    />
                  )}
                </span>
              </span>
            </Card>
          </li>
        ))}
        {q && resultados.length === 0 && <li className="text-sm text-texto/50">Sin resultados.</li>}
      </ul>
    </div>
  );
}

/**
 * Checkbox de asistencia de una persona, para usar bajo el rótulo
 * "Asistencia" en la fila de resultados. Un botón de color solo (sin
 * casilla) se leía como "ya entró" incluso sin marcar — la casilla
 * vacía/llena deja el estado inequívoco a simple vista.
 */
function CasillaAsistencia({
  etiqueta,
  presente,
  cargando,
  tactil,
  onClick,
}: {
  etiqueta: string;
  presente: boolean;
  cargando: boolean;
  tactil: boolean;
  onClick: (e: MouseEvent) => void;
}) {
  return (
    <button
      type="button"
      disabled={cargando}
      onClick={onClick}
      aria-pressed={presente}
      className={[
        'flex flex-1 items-center justify-center gap-1.5 rounded-full border font-medium transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none',
        tactil ? 'px-3 py-2 text-xs' : 'px-2.5 py-1.5 text-[11px]',
        presente
          ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
          : 'border-borde bg-white text-texto/60 hover:border-navy/30 hover:text-texto',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={[
          'flex shrink-0 items-center justify-center rounded-full border-2',
          tactil ? 'h-4 w-4' : 'h-3.5 w-3.5',
          presente ? 'border-emerald-500 bg-emerald-500' : 'border-texto/30 bg-white',
        ].join(' ')}
      >
        {presente && (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth={4}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-2.5 w-2.5"
          >
            <path d="M5 13l4 4L19 7" />
          </svg>
        )}
      </span>
      {cargando ? '…' : presente ? `${etiqueta} ✓` : etiqueta}
    </button>
  );
}
